import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useSendAiChatMessage, useGenerateAiChatSummary, useAiChatConversation } from '../../hooks/api';
import { useSpeechRecognition } from '../../hooks/useSpeechRecognition/useSpeechRecognition';
import { LoadingSpinner } from '../../components';
import { MicIcon, SoundWaveIcon } from '../../components/Icons';
import type { AiChatMessage, AiChatSummaryResponse } from '../../lib/api';
import { Send } from 'lucide-react';
import {
  Container,
  Header,
  BackButton,
  HeaderTitle,
  MessagesArea,
  MessageBubble,
  QuickRepliesContainer,
  QuickReplyButton,
  InputArea,
  TextInput,
  ButtonRow,
  SendButton,
  VoiceButton,
  TypingIndicator,
  SummaryCard,
  SummaryTitle,
  SummaryText,
  SummaryTopics,
  SummaryTopicTag,
  EndConversationLink,
  ReturnButton,
} from './AiChatConversationPage.styled';

/** DynamoDBに保存済みのJSON文字列contentからメッセージテキストを抽出する */
const extractMessageText = (content: string): string => {
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed === 'object' && parsed !== null && typeof parsed.message === 'string') {
      return parsed.message;
    }
  } catch {
    // not JSON, return as-is
  }
  return content;
};

interface LocationState {
  conversationId: string;
  topic?: string;
  initialMessage?: AiChatMessage;
}

export const AiChatConversationPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ conversationId: string }>();
  const state = location.state as LocationState | null;
  const conversationId = state?.conversationId || params.conversationId || null;

  const {
    data: conversationData,
    isLoading: isConversationLoading,
    error: conversationError,
  } = useAiChatConversation(!state?.initialMessage ? conversationId : null);

  const [messages, setMessages] = useState<AiChatMessage[]>([]);
  const [inputText, setInputText] = useState('');
  const [quickReplies, setQuickReplies] = useState<string[]>([]);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isEnded, setIsEnded] = useState(false);
  const [summary, setSummary] = useState<AiChatSummaryResponse | null>(null);
  const [isSummaryLoading, setIsSummaryLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const sendMessage = useSendAiChatMessage();
  const generateSummary = useGenerateAiChatSummary();
  const {
    state: voiceState,
    lastSegment,
    segmentId,
    startRecording,
    stopRecording,
    resetRecording,
  } = useSpeechRecognition();
  const isRecording = voiceState === 'recording';
  const isProcessing = voiceState === 'processing';

  // Initialize with the first AI message
  useEffect(() => {
    if (state?.initialMessage) {
      setMessages([state.initialMessage]);
      setQuickReplies(state.initialMessage.quick_replies || []);
    }
  }, [state?.initialMessage]);

  // URL直アクセス/リロード時に会話履歴を復元
  useEffect(() => {
    if (state?.initialMessage || !conversationData || messages.length > 0) {
      return;
    }

    setMessages(conversationData.messages);

    const lastAssistant = [...conversationData.messages].reverse().find(msg => msg.role === 'assistant');
    setQuickReplies(lastAssistant?.quick_replies || []);
  }, [state?.initialMessage, conversationData, messages.length]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isWaiting, summary]);

  // Voice input to text
  useEffect(() => {
    if (lastSegment) {
      setInputText(prev => prev + lastSegment);
    }
  }, [segmentId, lastSegment]);

  const handleSend = useCallback(async (content: string) => {
    if (!content.trim() || !conversationId || isWaiting) return;

    const trimmedContent = content.trim();
    setInputText('');
    setQuickReplies([]);

    // Add user message
    const userMsg: AiChatMessage = {
      message_id: `msg_${Date.now()}_user`,
      role: 'user',
      content: trimmedContent,
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setIsWaiting(true);

    try {
      const result = await sendMessage.mutateAsync({
        conversationId,
        content: trimmedContent,
      });

      setMessages(prev => [...prev, result.message]);
      setQuickReplies(result.message.quick_replies || []);
    } catch (error) {
      console.error('[AiChat] Send failed:', error);
      // Add error message
      setMessages(prev => [...prev, {
        message_id: `msg_${Date.now()}_error`,
        role: 'assistant',
        content: '申し訳ございません。通信エラーが発生いたしました。恐れ入りますが、もう一度お試しください。',
        created_at: new Date().toISOString(),
      }]);
    } finally {
      setIsWaiting(false);
    }
  }, [conversationId, isWaiting, sendMessage]);

  const handleQuickReply = useCallback((text: string) => {
    handleSend(text);
  }, [handleSend]);

  const handleVoiceToggle = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      resetRecording();
      startRecording();
    }
  }, [isRecording, startRecording, stopRecording, resetRecording]);


  const handleEndConversation = useCallback(async () => {
    if (!conversationId || isSummaryLoading || isEnded) return;

    setIsEnded(true);
    setQuickReplies([]);

    // AI からの温かい挨拶メッセージを追加
    const farewellMsg: AiChatMessage = {
      message_id: `msg_${Date.now()}_farewell`,
      role: 'assistant',
      content: '本日はお話しくださり、ありがとうございました。どうぞお身体をお大事になさってください。またいつでもお声がけくださいませ。',
      created_at: new Date().toISOString(),
    };
    setMessages(prev => [...prev, farewellMsg]);

    // 要約生成
    setIsSummaryLoading(true);
    try {
      const result = await generateSummary.mutateAsync({
        conversationId,
      });
      setSummary(result);
    } catch (error) {
      console.error('[AiChat] Summary generation failed:', error);
    } finally {
      setIsSummaryLoading(false);
    }
  }, [conversationId, isSummaryLoading, isEnded, generateSummary]);

  if (!conversationId) {
    return <Navigate to="/ai-chat" replace />;
  }

  if (!state?.initialMessage && isConversationLoading && messages.length === 0) {
    return <LoadingSpinner fullPage message="読み込み中..." />;
  }

  if (!state?.initialMessage && conversationError) {
    return <Navigate to="/ai-chat" replace />;
  }

  const moodLabel: Record<string, string> = {
    positive: '楽しいお話でした',
    neutral: '穏やかなお話でした',
    concerned: 'お気持ちをお聞かせいただきました',
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/ai-chat')}>
          ← もどる
        </BackButton>
        <HeaderTitle>おはなしの部屋</HeaderTitle>
      </Header>

      <MessagesArea>
        {messages.map((msg) => (
          <MessageBubble key={msg.message_id} $isUser={msg.role === 'user'}>
            {msg.role === 'user' ? msg.content : extractMessageText(msg.content)}
          </MessageBubble>
        ))}

        {isWaiting && (
          <TypingIndicator>
            <span />
            <span />
            <span />
          </TypingIndicator>
        )}

        {isEnded && isSummaryLoading && (
          <TypingIndicator>
            <span />
            <span />
            <span />
          </TypingIndicator>
        )}

        {summary && (
          <SummaryCard>
            <SummaryTitle>本日のお話の記録</SummaryTitle>
            <SummaryText>{summary.summary}</SummaryText>
            {summary.topics.length > 0 && (
              <SummaryTopics>
                {summary.topics.map((topic, i) => (
                  <SummaryTopicTag key={i}>{topic}</SummaryTopicTag>
                ))}
              </SummaryTopics>
            )}
            {summary.mood && moodLabel[summary.mood] && (
              <SummaryText style={{ marginTop: '8px', color: '#8B7E74' }}>
                {moodLabel[summary.mood]}
              </SummaryText>
            )}
          </SummaryCard>
        )}

        <div ref={messagesEndRef} />
      </MessagesArea>

      {!isEnded && quickReplies.length > 0 && !isWaiting && (
        <QuickRepliesContainer>
          {quickReplies.map((reply, index) => (
            <QuickReplyButton
              key={index}
              onClick={() => handleQuickReply(reply)}
              disabled={isWaiting}
            >
              {reply}
            </QuickReplyButton>
          ))}
        </QuickRepliesContainer>
      )}

      {isEnded && !isSummaryLoading && summary && (
        <ReturnButton onClick={() => navigate('/')}>
          ホームにもどる
        </ReturnButton>
      )}

      {!isEnded && (
        <InputArea>
          <TextInput
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}

            placeholder="メッセージを書く"
            disabled={isWaiting}
          />
          <ButtonRow>
            <VoiceButton
              type="button"
              $isRecording={isRecording || isProcessing}
              onClick={handleVoiceToggle}
              disabled={isWaiting || isProcessing}
            >
              {isRecording ? <SoundWaveIcon /> : <MicIcon />}
              {isRecording ? '聞き取り中...' : isProcessing ? '変換中...' : '音声入力'}
            </VoiceButton>
            <SendButton
              type="button"
              $hidden={isRecording || isProcessing}
              onClick={() => handleSend(inputText)}
              disabled={!inputText.trim() || isWaiting}
            >
              <Send size={20} />
              送信する
            </SendButton>
          </ButtonRow>
          {messages.length >= 2 && !isWaiting && (
            <EndConversationLink
              onClick={handleEndConversation}
              disabled={isSummaryLoading}
            >
              本日はこのあたりで失礼いたします
            </EndConversationLink>
          )}
        </InputArea>
      )}
    </Container>
  );
};

export default AiChatConversationPage;
